import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { getToken, getRole, getUser } from "../utils/authStorage";
import {
  isSuperAdminUser,
  isQaTrackUser,
  hasPermission,
  normalizeRoleKey,
  getSafeRedirectPathForUser,
  canAccessStaffConsole,
} from "../utils/permissions";

/**
 * Default path when authenticated user is not allowed on this route.
 */
function getDefaultPathForRole(role, user) {
  const u = user || getUser();
  if (u && isSuperAdminUser(u)) return "/super-admin-dashboard";
  if (u && isQaTrackUser(u)) return "/qa-dashboard";
  const key = normalizeRoleKey(role);
  if (key === "SUPER_ADMIN") return "/super-admin-dashboard";
  if (key === "ADMIN" || key === "ADMINISTRATOR") {
    return getSafeRedirectPathForUser(u);
  }
  if (key === "DOCTOR") return "/DoctorDashboard";
  if (["QUALITY_ASSURANCE", "QA", "EDITOR", "TRANSCRIPTIONIST", "TRANSCRIPTION", "MT", "MEDICAL_TRANSCRIPTIONIST", "MEDICAL_TRANSCRIPTION"].includes(key)) {
    return "/qa-dashboard";
  }
  return "/login";
}

/**
 * Protects routes: authentication, optional role allowlist (QA flows), super admin, permission keys, or staff console access.
 * Auth is stored in sessionStorage (per-tab).
 *
 * Props:
 * - allowedRoles: legacy QA/editor routes (normalized role match)
 * - requireSuperAdmin: only Super Admin
 * - requirePermission: single permission string (super admin passes)
 * - requireAnyPermission: array — user needs at least one
 * - staffConsole: any staff with at least one dashboard tab or S3 access (or super admin)
 * - allowQaTrack: QA / Editor / MT / Transcriptionist product users (see isQaTrackUser)
 */
export default function ProtectedRoute({
  children,
  allowedRoles,
  allowQaTrack,
  requireSuperAdmin,
  requirePermission,
  requireAnyPermission,
  staffConsole,
}) {
  const location = useLocation();
  const token = getToken();
  const role = getRole();
  const user = getUser();

  if (!token) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requireSuperAdmin) {
    if (!isSuperAdminUser(user)) {
      return <Navigate to={getSafeRedirectPathForUser(user)} replace />;
    }
    return children;
  }

  if (staffConsole) {
    if (!canAccessStaffConsole(user)) {
      return <Navigate to={getSafeRedirectPathForUser(user)} replace />;
    }
  }

  if (allowQaTrack) {
    if (!isQaTrackUser(user)) {
      return <Navigate to={getSafeRedirectPathForUser(user)} replace />;
    }
    return children;
  }

  if (requirePermission) {
    if (!hasPermission(user, requirePermission)) {
      return <Navigate to={getSafeRedirectPathForUser(user)} replace />;
    }
  }

  if (requireAnyPermission && Array.isArray(requireAnyPermission) && requireAnyPermission.length > 0) {
    const ok = requireAnyPermission.some((p) => hasPermission(user, p));
    if (!ok) {
      return <Navigate to={getSafeRedirectPathForUser(user)} replace />;
    }
  }

  if (allowedRoles && Array.isArray(allowedRoles) && allowedRoles.length > 0) {
    const normalizedRole = normalizeRoleKey(role);
    const allowed = allowedRoles.some((r) => normalizeRoleKey(r) === normalizedRole);
    if (!allowed) {
      const redirectTo = getDefaultPathForRole(role, user);
      return <Navigate to={redirectTo} replace />;
    }
  }

  return children;
}
